import React, { useState, useContext, useCallback } from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { node } from "prop-types";
import { Z, CHROME_INSET } from "../styles/zLayers";
import { claimRefusals } from "../utils/refusalNotice";

const SnackbarContext = React.createContext(null);
export const SnackBarProvider = ({ children }) => {
  const [isSnackOpen, setOpenSnack] = useState(false);
  const [snackMessage, setSnackMessage] = useState("in snackbar!");
  const [snackColor, setSnackColor] = useState("success");
  const [snackVariant, setSnackVariant] = useState("filled");
  // Bumped on every raise, and used as the Snackbar's key so a new toast
  // remounts. MUI arms the auto-hide timer from an effect whose deps are
  // [open, autoHideDuration, setAutoHideTimer] (the last a stable
  // useEventCallback); the message travels in `children` and is in none of them,
  // so while `open` stays true a second toast inherits whatever is left of the
  // first one's countdown. Keying on the message instead would not do: two
  // raises of the SAME string leave the key unchanged and nothing remounts, and
  // the failures useAxios does snack are the ones that repeat verbatim: an
  // expired token, a forbidden action, a server that is down, a network that is
  // gone. extractError answers a responseless failure out of a fixed
  // USER_MESSAGES table ("Network error. Please check your connection and try
  // again."), so two in a row commonly carry an identical message. A refusal
  // (another 4xx on an action) is held back for one task and raised only if no
  // screen showed it first (B-31, src/utils/refusalNotice.js).
  const [snackSeq, setSnackSeq] = useState(0);
  const setSnack = useCallback(
    (color, message, variant = "filled") => {
      // Whatever a screen just said replaces a refusal toast useAxios is
      // holding back: the screen's own message is the one meant for this
      // moment, and a second toast would only replace it (B-31).
      claimRefusals();
      setOpenSnack(true);
      setSnackColor(color);
      setSnackMessage(message);
      setSnackVariant(variant);
      setSnackSeq((n) => n + 1);
    },
    []
  );
  // MUI passes a reason. 'clickaway' comes from a document-level click listener
  // it installs around every Snackbar, so without this guard the user's next tap
  // anywhere killed the toast, however early (#193). 'timeout' and
  // 'escapeKeyDown' must still close: the first is the toast's own life ending,
  // the second is a deliberate dismissal.
  const closeSnack = useCallback((_event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnack(false);
  }, []);
  return (
    <>
      <Snackbar
        key={snackSeq}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={isSnackOpen}
        onClose={closeSnack}
        autoHideDuration={3000}
        // Top-right is exactly where the floating account bar lives, so every
        // snack used to land on the avatar. Drop below that band, and stay
        // inside the app's 520px column rather than hugging the viewport edge.
        sx={{
          zIndex: Z.snackbar,
          top: `calc(${CHROME_INSET.top}px + var(--safe-area-inset-top, env(safe-area-inset-top, 0px))) !important`,
          right: {
            xs: "16px !important",
            sm: "max(16px, calc((100vw - 520px) / 2 + 16px)) !important",
          },
        }}
      >
        <Alert severity={snackColor} variant={snackVariant}>
          {snackMessage}
        </Alert>
      </Snackbar>

      <SnackbarContext.Provider value={setSnack}>
        {children}
      </SnackbarContext.Provider>
    </>
  );
};
SnackBarProvider.propTypes = {
  children: node.isRequired,
};
/**
 * useSnack for a component that may render outside the provider (OnboardingCoach
 * is mounted in Layout and also rendered alone in its tests): a no-op there.
 */
export const useOptionalSnack = () => useContext(SnackbarContext) || (() => {});

export const useSnack = () => {
  const context = useContext(SnackbarContext);
  if (!context)
    throw new Error("useSnack must be used within a SnackbarProvider");
  return context;
};
